import {
  AccountOption,
  Button,
  Flash,
  FormField,
  Input,
  Link
} from '@cma/common'
import {
  AccountBanner,
  SelectAccountCard,
  platformLauncherUrl,
  requiresPlatformLogin,
  UseDifferentEmailButton,
  useAccountOptions,
  useRequestResetPassword
} from '@cma/features/auth'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

type Step = 'form' | 'select'

const schema = yup.object({
  email: yup.string().email().required('Your email is required')
})

// !This doesn't work locally - passes on staging. Backend is aware.
// !Pull reset password URL from CMA backend log
export default function PasswordRequestReset() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialEmail = searchParams.get('email') || ''
  const accountId = searchParams.get('accountId') || undefined
  const initialAccountName = searchParams.get('accountName') || undefined
  const [step, setStep] = useState<Step>('form')
  const [emailLocked, setEmailLocked] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState(initialEmail)
  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [pendingAccountName, setPendingAccountName] =
    useState(initialAccountName)
  const shouldFocusEmail = useRef(false)
  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    watch,
    formState: { errors }
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: { email: initialEmail }
  })
  const emailValue = watch('email')
  const {
    mutate: requestResetPassword,
    isLoading: isSubmitting,
    error: submitError
  } = useRequestResetPassword()
  const {
    mutate: lookupAccounts,
    isLoading: isLookingUp,
    error: lookupError
  } = useAccountOptions()

  useEffect(() => {
    if (step === 'form' && shouldFocusEmail.current) {
      shouldFocusEmail.current = false
      setFocus('email')
    }
  })

  function clearResolvedAccount() {
    setEmailLocked(false)
    setPendingAccountName(undefined)
    setSearchParams({})
  }

  function startOver() {
    shouldFocusEmail.current = true
    setAccounts([])
    clearResolvedAccount()
    setValue('email', submittedEmail)
    setStep('form')
  }

  function submitReset(email: string, accountId?: string) {
    requestResetPassword(
      { email, accountId },
      {
        onSuccess() {
          navigate('/login', { state: { passwordResetRequested: true } })
        }
      }
    )
  }

  if (step === 'select') {
    return (
      <SelectAccountCard
        accounts={accounts}
        email={submittedEmail}
        heading="Select an account"
        description={
          <>
            We found more than one Cloud CMA account associated with{' '}
            <span className="font-semibold">{submittedEmail}</span>. Select the
            account you want to reset the password for.
          </>
        }
        onContinue={(account) => {
          setSearchParams({
            email: submittedEmail,
            accountId: account.accountId
          })
          setEmailLocked(true)
          setPendingAccountName(account.name)
          setStep('form')
        }}
        onUseDifferentEmail={startOver}
      />
    )
  }

  return (
    <>
      <div className="space-y-4 rounded-lg bg-white px-8 py-6 shadow">
        <h1 className="mb-6 text-center text-2xl font-medium">
          Need your password reset?
        </h1>
        <p className="text-xs">
          Please enter your email and we'll send you instructions on how to
          reset your password
        </p>
        <form
          className="space-y-4"
          onSubmit={handleSubmit((data) => {
            if (accountId) {
              submitReset(data.email, accountId)
              return
            }

            setSubmittedEmail(data.email)
            lookupAccounts(data.email, {
              onSuccess(options) {
                if (!options.length) {
                  submitReset(data.email)
                  return
                }

                if (options.length === 1) {
                  if (requiresPlatformLogin(options[0])) {
                    window.location.href = platformLauncherUrl
                    return
                  }

                  setSearchParams({
                    email: data.email,
                    accountId: options[0].accountId
                  })
                  submitReset(data.email, options[0].accountId)
                  return
                }

                setAccounts(options)
                setStep('select')
              }
            })
          })}>
          <FormField label="Email" error={errors.email?.message}>
            <Input
              placeholder="john@acmerealty.com"
              {...register('email', {
                onChange() {
                  if (accountId) clearResolvedAccount()
                }
              })}
              type="email"
              readOnly={emailLocked}
            />
          </FormField>
          {pendingAccountName && (
            <AccountBanner>Sending email to {pendingAccountName}</AccountBanner>
          )}
          <div className="flex justify-center">
            <Button
              loading={isSubmitting || isLookingUp}
              disabled={!emailValue?.trim()}>
              {accountId ? 'Email Instructions' : 'Continue'}
            </Button>
          </div>
          {(submitError || lookupError) && (
            <Flash variant="error">
              {(submitError || lookupError)?.message}
            </Flash>
          )}
        </form>
      </div>
      {accountId && <UseDifferentEmailButton onClick={startOver} />}
      <div className="flex">
        <Link
          to="/login"
          className="helix-text-brand mx-auto text-xs font-medium hover:no-underline">
          Return to Login
        </Link>
      </div>
    </>
  )
}
